<script>
// Persist + restore for the primary list views.
//
// Every list already kept its filter/sort/page in a per-module Vuex store, but
// each one hand-rolled the same save/restore block, and three of them
// (contacts, newContacts, users) wrote their date quick-filters into the
// 'activities' module while reading them back from their own - so those dates
// could never restore, and they trampled the Activities list on the way past.
// Routing every read and write through this mixin's `listViewModule` makes that
// class of mistake impossible.
//
// Scope: this owns *persist and restore* only. Building the filters - quick
// filter indices, permission-level defaults, saved-view lookups - stays in each
// view, because that part genuinely differs per list.
//
// Usage:
//   import ListViewStateMixin from '../ListViewStateMixin'
//   export default {
//     mixins: [ListViewStateMixin],
//     listViewModule: 'customers',   // Vuex module holding this list's state
//     ...
//   }

export default {
  computed: {
    // Vuex module namespace for this list. Declared as a component option
    // rather than derived from $route.name, because a view can be reachable
    // from more than one route and the two names do not always match the
    // module (/activities -> MyActivities.vue -> 'myactivities').
    listViewModule() {
      return this.$options.listViewModule
    }
  },
  methods: {
    listViewGet(getterName) {
      return this.$store.getters[`${this.listViewModule}/${getterName}`]
    },
    listViewSet(actionName, payload) {
      return this.$store.dispatch(
        `${this.listViewModule}/${actionName}`,
        payload
      )
    },

    // ---- persist --------------------------------------------------------

    persistSort(sortField, sortFieldId, sortOrder) {
      this.listViewSet("setSort", { sortField, sortFieldId, sortOrder });
    },
    persistPage(page, rows) {
      this.listViewSet("setPage", { page, rows });
    },
    persistQuickFilters(value) {
      this.listViewSet("setMainListQuickFilters", value);
    },
    persistDatesFrom(value) {
      this.listViewSet("setQuickFiltersDatesFrom", value);
    },
    persistDatesTo(value) {
      this.listViewSet("setQuickFiltersDatesTo", value);
    },
    persistSavedFilter(value) {
      this.listViewSet("setMainListSavedFilter", value);
    },
    persistSearchText(value) {
      this.listViewSet("setSearchText", value || "");
    },

    // The search text lives in three places: the component's input, Vuex, and
    // the URL query - the URL so a filtered list stays linkable and so Back and
    // the breadcrumb return to it. This is the one place that writes Vuex and
    // the URL together; every path that changes the search (typing, the clear
    // button, the hard-load reset) must come through here, or the three drift
    // apart and the next return to the list resurrects a search the user had
    // cleared, from the stale URL.
    //
    // replace() rather than push(): every keystroke used to add a history
    // entry, so Back walked through the search letter by letter. An empty
    // search removes the key rather than leaving "?search=" behind.
    syncSearchToUrl(value) {
      const text = value || "";
      this.persistSearchText(text);

      const query = this.$route.query || {};
      if ((query.search || "") === text) return;

      const next = { ...query };
      if (text) {
        next.search = text;
      } else {
        delete next.search;
      }
      this.$router.replace({ query: next }).catch(() => {});
    },

    // ---- restore --------------------------------------------------------

    // Everything the list needs to put itself back the way the user left it.
    // Arrays and objects are copied so the caller can assign them straight onto
    // component data without aliasing store state.
    readListViewState() {
      return {
        sortField: this.listViewGet("getSortField"),
        sortFieldId: this.listViewGet("getSortFieldId"),
        sortOrder: this.listViewGet("getSortOrder"),
        page: this.listViewGet("getPage"),
        rows: this.listViewGet("getRows"),
        searchText: this.listViewGet("getSearchText") || "",
        quickFilters: [...(this.listViewGet("getMainListQuickFilter") || [])],
        datesFrom: [...(this.listViewGet("getQuickFiltersDatesFrom") || [])],
        datesTo: [...(this.listViewGet("getQuickFiltersDatesTo") || [])],
        savedFilter: { ...(this.listViewGet("getMainListSavedFilter") || {}) }
      };
    },

    // Clears the persisted state. Called by the views' hard-load path, which
    // deliberately resets the list back to its defaults.
    clearListViewState() {
      this.persistSort("", 0, 0);
      this.persistPage(1, 25);
      this.persistQuickFilters([]);
      this.persistDatesFrom([]);
      this.persistDatesTo([]);
      this.persistSearchText("");
      this.persistScrollY(0);
      this.persistGridScrollY(0);
    },

    // ---- scroll ---------------------------------------------------------
    //
    // Two different things can scroll, depending on the list:
    //
    //   window          the layout itself has no inner scroller (.layout-content
    //                   is just padding/flex, .layout-main is min-height:100vh),
    //                   so on a full-height grid the document scrolls. Customers
    //                   behaves this way.
    //   the grid body   lists rendered with a PrimeVue scrollable DataTable keep
    //                   their rows in .p-datatable-scrollable-body, which scrolls
    //                   internally while the window stays at 0. Contacts behaves
    //                   this way.
    //
    // Capturing only the window silently stored 0 for the second kind and
    // restored nothing, so both are handled. A scrollable table renders a pair
    // of these bodies (frozen + unfrozen) that scroll together.

    currentScrollY() {
      return (
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        0
      );
    },
    listScrollBodies() {
      return Array.prototype.slice
        .call(document.querySelectorAll(".p-datatable-scrollable-body"))
        .filter(el => el.scrollHeight > el.clientHeight + 5);
    },
    currentGridScrollY() {
      const tops = this.listScrollBodies().map(el => el.scrollTop);
      return tops.length ? Math.max.apply(null, tops) : 0;
    },
    persistScrollY(value) {
      this.listViewSet("setScrollY", value);
    },
    persistGridScrollY(value) {
      this.listViewSet("setGridScrollY", value);
    },
    captureListScroll() {
      this.persistScrollY(this.currentScrollY());
      this.persistGridScrollY(this.currentGridScrollY());
    },

    // Put the scroll offset back. Safe to call as soon as the list decides it
    // is being re-entered - it waits for the page to actually grow tall enough
    // to hold the offset, which is the bit that matters: the Syncfusion grids
    // load their rows asynchronously, and scrolling against a still-empty grid
    // clamps to the top and silently loses the position.
    //
    // The budget is deliberately generous (~20s). These list endpoints can take
    // the best part of ten seconds to answer, and giving up early is not a
    // harmless no-op: scrolling while the grid is still short clamps the offset
    // to 0 and loses the position for good. Waiting costs nothing, because the
    // loop stops the moment the page is tall enough.
    restoreListScroll(attempt, stableFor) {
      // The retry below can still be pending when the user navigates on, so
      // bail if this list is no longer the page being shown - otherwise we
      // would yank whatever replaced it to an unrelated offset.
      if (this._isDestroyed || this._isBeingDestroyed) return;

      const y = this.listViewGet("getScrollY");
      const gridY = this.listViewGet("getGridScrollY");
      if (!y && !gridY) return;

      const tries = attempt || 0;
      if (tries > 130) return;

      const bodies = this.listScrollBodies();
      const near = (a, b) => Math.abs(a - b) <= 2;

      // Where things stand right now, measured before we touch anything.
      const atTarget = () =>
        (!y || near(this.currentScrollY(), y)) &&
        (!gridY || bodies.some(el => near(el.scrollTop, gridY)));
      const atTop = () =>
        (!y || this.currentScrollY() === 0) &&
        (!gridY || bodies.every(el => el.scrollTop === 0));

      const apply = () => {
        if (y) window.scrollTo(0, y);
        if (gridY) bodies.forEach(el => (el.scrollTop = gridY));
      };

      // Setting the offset once is not enough. This runs from created(), so the
      // first attempt lands while the outgoing page is still mounted: the scroll
      // appears to take, then the grid renders, the page briefly collapses and
      // the browser clamps the offset back to the top - silently, with nothing
      // to hook. So we come back and check whether it actually held.
      //
      // The check has to happen at the START of a tick, before re-applying.
      // Measuring straight after setting the value always says "held", which
      // makes the stability count meaningless, and re-applying first would
      // overwrite a scroll the user had just made before we could notice it.
      let settled = stableFor || 0;

      if (tries === 0) {
        apply();
        settled = 0;
      } else if (atTarget()) {
        // Still where we put it. A few consecutive holds and we can stand down,
        // since the grid may re-render more than once as its rows arrive.
        settled += 1;
        if (settled >= 4) return;
      } else if (atTop()) {
        // Clamped back to the top by a re-render - put it back.
        apply();
        settled = 0;
      } else {
        // Somewhere else entirely: the user has scrolled. Fighting them would
        // be worse than losing the position.
        return;
      }

      window.setTimeout(() => this.restoreListScroll(tries + 1, settled), 150);
    }
  },

  // Fires before the navigation is confirmed, so the window is still scrolled
  // where the user left it. Capturing in beforeDestroy would be too late - by
  // then the router has already updated the DOM.
  beforeRouteLeave(to, from, next) {
    this.captureListScroll();
    next();
  }
};
</script>
